import React, { useState, useMemo, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Tabs, Popup } from 'react-vant'
import Header from '@/components/Header'
import Back from '@/components/Back'
import PageLayout from '@/components/PageLayout'
import CustomButton from '@/components/CustomButton'
import { helpCategories, getAllArticles, type HelpArticle } from './helpData'
import { APP_NAME } from '@/config/constants'

export default React.memo(function Help() {
    const { t } = useTranslation()
    const [activeTab, setActiveTab] = useState(0)

    // i18n 插值参数
    const i18nParams = useMemo(() => ({ appName: APP_NAME }), [])

    // 搜索相关
    const [isSearching, setIsSearching] = useState(false)
    const [searchText, setSearchText] = useState('')
    const searchInputRef = useRef<HTMLInputElement>(null)

    // 文章详情弹窗
    const [detailVisible, setDetailVisible] = useState(false)
    const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null)

    // 搜索结果 - 仅搜索标题
    const searchResults = useMemo(() => {
        if (!searchText.trim()) return []
        const keyword = searchText.toLowerCase()
        return getAllArticles().filter((article) =>
            t(article.questionKey, i18nParams).toLowerCase().includes(keyword)
        )
    }, [searchText, t, i18nParams])

    // 高亮关键词
    const highlightText = useCallback((text: string, keyword: string) => {
        if (!keyword.trim()) return text
        const regex = new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
        const parts = text.split(regex)
        return parts.map((part, i) =>
            regex.test(part)
                ? <span key={i} className="text-[var(--primary-color)]">{part}</span>
                : part
        )
    }, [])

    // 打开搜索
    const handleOpenSearch = useCallback(() => {
        setIsSearching(true)
        setTimeout(() => searchInputRef.current?.focus(), 100)
    }, [])

    // 关闭搜索
    const handleCloseSearch = useCallback(() => {
        setIsSearching(false)
        setSearchText('')
    }, [])

    // 打开文章详情
    const handleOpenArticle = useCallback((article: HelpArticle) => {
        setSelectedArticle(article)
        setDetailVisible(true)
    }, [])

    // 关闭文章详情
    const handleCloseArticle = useCallback(() => {
        setDetailVisible(false)
        setSelectedArticle(null)
    }, [])

    // 在线客服
    const handleContactService = useCallback(() => {
        window.open('https://direct.lc.chat/19572076/')
    }, [])

    // 渲染文章列表项
    const renderArticleItem = useCallback((article: HelpArticle, keyword?: string, index?: number) => {
        const questionText = t(article.questionKey, i18nParams)
        return (
            <div
                key={article.id}
                className="flex items-center justify-between py-4 border-b border-[#F2F2F2] last:border-b-0 cursor-pointer active:bg-gray-50"
                onClick={() => handleOpenArticle(article)}
            >
                <div className="flex items-center flex-1 mr-3">
                    {keyword && (
                        <div className="w-5 h-5 mr-2 flex-shrink-0">
                            <img src="/icon/file-description.svg" alt="" />
                        </div>
                    )}
                    <span className="text-base text-[#1D2129] leading-5">
                        {index != null && <span className="mr-1">{index}.</span>}
                        {keyword ? highlightText(questionText, keyword) : questionText}
                    </span>
                </div>
                <img src="/icon/right-info.svg" className="w-4 h-2 flex-shrink-0 opacity-40" />
            </div>
        )
    }, [handleOpenArticle, highlightText, t, i18nParams])

    // 渲染文本内容（将 \n 转为换行）
    const renderTextContent = useCallback((text: string) => {
        const lines = text.split('\n')
        return lines.map((line, index) => (
            <React.Fragment key={index}>
                {line}
                {index < lines.length - 1 && <br />}
            </React.Fragment>
        ))
    }, [])

    // 渲染文章详情中的内容块
    const renderBlocks = useCallback((article: HelpArticle) => {
        return article.blocks.map((block, index) => {
            const text = t(block.key, i18nParams)
            if (block.type === 'subtitle') {
                return (
                    <p key={index} className="text-4 font-medium text-[#1A1A1A] mb-3 leading-6">
                        {text}
                    </p>
                )
            }
            // type === 'text'
            return (
                <div key={index} className="text-4 text-[#667085] font-normal leading-8 mb-3">
                    {renderTextContent(text)}
                </div>
            )
        })
    }, [t, i18nParams, renderTextContent])

    // ===================== 搜索模式 =====================
    if (isSearching) {
        return (
            <PageLayout isHeader={false}>
                {/* 搜索头部 */}
                <div className="flex items-center px-4 py-3 bg-white sticky top-0 z-2">
                    <div className="flex items-center flex-1 h-10 bg-[#F3F3F3] rounded-full px-4">
                        <img src="/icon/search-icon.svg" className="w-4 h-4 mr-2" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder={t('help_search_input')}
                            className="flex-1 bg-transparent outline-none text-base text-[#1A1A1A] placeholder-[#1A1A1A]/40"
                        />
                        {searchText && (
                            <div
                                className="w-5 h-5 flex items-center justify-center cursor-pointer"
                                onClick={() => setSearchText('')}
                            >
                                <svg viewBox="0 0 20 20" className="w-4 h-4">
                                    <circle cx="10" cy="10" r="10" fill="#C4C4C4" />
                                    <path d="M6.5 6.5L13.5 13.5M13.5 6.5L6.5 13.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                            </div>
                        )}
                    </div>
                    <span
                        className="ml-3 text-base text-[#1A1A1A] cursor-pointer flex-shrink-0"
                        onClick={handleCloseSearch}
                    >
                        {t('取消')}
                    </span>
                </div>

                {/* 搜索结果 */}
                {searchText && (
                    <div className="px-5">
                        {searchResults.length > 0 ? (
                            searchResults.map((article, idx) => renderArticleItem(article, searchText, idx + 1))
                        ) : (
                            <div className="text-center py-20 text-gray-400 text-sm">
                                {t('help_no_results')}
                            </div>
                        )}
                    </div>
                )}

                {/* 文章详情弹窗 */}
                <Popup
                    visible={detailVisible}
                    onClose={handleCloseArticle}
                    position="bottom"
                    round
                    style={{ height: '90%' }}
                    closeable
                    closeIcon={<img src="/icon/close.svg" alt="" />}
                    closeIconPosition="top-right"
                >
                    {selectedArticle && (
                        <div className="px-6 pt-8 pb-10 overflow-y-auto" style={{ maxHeight: '80vh' }}>
                            <h2 className="text-7 font-semibold text-[#1A1A1A] mb-4 pr-6 leading-9">
                                {t(selectedArticle.questionKey, i18nParams)}
                            </h2>
                            {renderBlocks(selectedArticle)}
                        </div>
                    )}
                </Popup>
            </PageLayout>
        )
    }

    // ===================== 主页模式 =====================
    return (
        <PageLayout>
            <Header
                className='!bg-[#F4F6F9]'
                leftChildren={<Back className='!bg-white'/>}
            />

            <div className="flex flex-col h-[calc(100vh-3.5rem)] overflow-hidden">
                {/* 顶部欢迎区域 */}
                <div className="bg-[#F4F6F9] rounded-b-6 pb-6 flex-shrink-0">
                    {/* 头像组 */}
                    <div className="flex justify-center items-center pt-3 pb-4 gap-4">
                        <div className="w-15 h-15 rounded-full bg-white flex items-center justify-center overflow-hidden">
                            <img src="/icon/help-icon-1.svg" alt="" />
                        </div>
                        <div className="w-15 h-15 rounded-full bg-white flex items-center justify-center overflow-hidden">
                            <img src="/icon/help-icon-2.svg" alt="" />
                        </div>
                        <div className="w-15 h-15 rounded-full bg-white flex items-center justify-center overflow-hidden">
                            <img src="/icon/help-icon-3.svg" alt="" />
                        </div>
                    </div>

                    {/* 标题 */}
                    <h1 className="text-xl font-semibold text-[#1A1A1A] text-center mb-5">
                        {t('help_welcome_title', i18nParams)}
                    </h1>

                    {/* 搜索栏 */}
                    <div className="mx-5">
                        <div
                            className="flex items-center h-12 bg-white rounded-full px-4 shadow-sm cursor-pointer"
                            onClick={handleOpenSearch}
                        >
                            <img src="/icon/search-icon.svg" className="w-5 h-5 mr-3" />
                            <span className="text-sm text-[#1A1A1A]/40">{t('help_search_placeholder')}</span>
                        </div>
                    </div>
                </div>

                {/* Tabs 分类区域 */}
                <div className="flex-1 flex flex-col overflow-hidden help-tabs pt-5">
                    <Tabs
                        active={activeTab}
                        onChange={(name) => setActiveTab(typeof name === 'string' ? parseInt(name) : name)}
                        swipeable
                        animated
                        color="#1D2129"
                        titleActiveColor="#1A1A1A"
                        titleInactiveColor="#8B8B8B"
                        lineWidth={28}
                        lineHeight={3}
                    >
                        {helpCategories.map((category, index) => (
                            <Tabs.TabPane key={category.key} title={t(category.labelKey)} name={index}>
                                <div className="px-5 overflow-y-auto pt-5" style={{ height: 'calc(100vh - 22rem)' }}>
                                    {category.articles.map((article, idx) => renderArticleItem(article, undefined, idx + 1))}
                                    {/* 底部留白，给在线客服按钮腾出空间 */}
                                    <div className="h-20" />
                                </div>
                            </Tabs.TabPane>
                        ))}
                    </Tabs>
                </div>

                {/* 底部在线客服按钮 */}
                <div className="fixed bottom-0 left-0 right-0 z-10 px-5 pb-6 pt-3 max-w-[750px] mx-auto bg-gradient-to-t from-white via-white to-transparent">
                    <CustomButton color="primary" onClick={handleContactService}>
                        <div className="flex items-center justify-center">
                            <img src="/icon/customer-service.svg" alt="" />
                            <span className='ml-2'>{t('help_contact_service')}</span>
                        </div>
                    </CustomButton>
                </div>
            </div>

            {/* 文章详情弹窗 */}
            <Popup
                visible={detailVisible}
                onClose={handleCloseArticle}
                position="bottom"
                round
                style={{ height: '90%' }}
                closeable
                closeIcon={<img src="/icon/close.svg" alt="" />}
                closeIconPosition="top-right"
            >
                {selectedArticle && (
                    <div className="px-6 pt-8 pb-10 overflow-y-auto" style={{ maxHeight: '80vh' }}>
                        {/* 标题 */}
                        <h2 className="text-7 font-semibold text-[#1A1A1A] mb-4 pr-6 leading-9">
                            {t(selectedArticle.questionKey, i18nParams)}
                        </h2>
                        {/* 内容块 */}
                        {renderBlocks(selectedArticle)}
                    </div>
                )}
            </Popup>
        </PageLayout>
    )
})
